import React, {
  useState, useMemo, useRef, useEffect,
} from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  InputGroup,
} from '@openedx/paragon';
import { Add, Search, ExpandMore } from '@openedx/paragon/icons';

interface Company {
  company_name: string;
  friendly_name: string;
  size: string;
  website?: string;
  admins: string[];
  primary_admin_email: string;
  custom_csv_fields: string[];
  custom_admin_portal: string[];
}

interface CompanySelectorProps {
  companies: Company[];
  selectedCompany: Company | null;
  onCompanySelect: (company: Company | null) => void;
  onCompanyCreate: (companyData: Partial<Company>) => void;
  isCreatingCompany: boolean;
}

const COMPANY_SIZE_OPTIONS = [
  'Less than 999',
  '1000 - 4999',
  '5000 - 9999',
  '10000+',
];

const CompanySelector: React.FC<CompanySelectorProps> = ({
  companies,
  selectedCompany,
  onCompanySelect,
  onCompanyCreate,
  isCreatingCompany,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showInlineForm, setShowInlineForm] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [newCompanyData, setNewCompanyData] = useState({
    friendly_name: '',
    admins: [''],
    primary_admin_email: '',
    size: '',
    website: '',
    edx_course_org: '',
    skill_select: {
      enabled: false,
      total_available: null as number | null,
      limit: null as number | null,
    },
  });

  // Filter companies based on search term
  const filteredCompanies = useMemo(() => {
    if (!searchTerm) { return companies; }
    return companies.filter(company => company.friendly_name.toLowerCase().includes(searchTerm.toLowerCase())
      || company.company_name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [companies, searchTerm]);

  // Show create option when there's a search term but no exact match
  const showCreateOption = searchTerm.length > 2 && (
    filteredCompanies.length === 0
    || !filteredCompanies.some(c => c.friendly_name.toLowerCase() === searchTerm.toLowerCase()
      || c.company_name.toLowerCase() === searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchTerm(value);
    setIsDropdownOpen(true);
    setHighlightedIndex(-1);

    // Clear selected company if user is searching for something different
    if (selectedCompany) {
      const selectedDisplayName = `${selectedCompany.friendly_name} (${selectedCompany.company_name})`;
      if (value !== selectedDisplayName) {
        // User is typing something different, clear the selection
        onCompanySelect(null);
      }
    }
  };

  const handleInputFocus = () => {
    setIsDropdownOpen(true);
  };

  const handleCompanySelect = (company: Company) => {
    onCompanySelect(company);
    setSearchTerm(`${company.friendly_name} (${company.company_name})`);
    setIsDropdownOpen(false);
    setHighlightedIndex(-1);
  };

  const handleCreateNew = () => {
    setShowInlineForm(true);
    setIsDropdownOpen(false);
    // Reset form and pre-fill the friendly name from search term
    const friendlyName = searchTerm.trim();
    setNewCompanyData({
      friendly_name: friendlyName,
      admins: [''],
      primary_admin_email: '',
      size: '',
      website: '',
      edx_course_org: '',
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const currentCompanies = !searchTerm ? companies : filteredCompanies;
    const totalOptions = currentCompanies.length + (showCreateOption ? 1 : 0);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!isDropdownOpen) {
          setIsDropdownOpen(true);
        } else {
          setHighlightedIndex(prev => (prev + 1) % totalOptions);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (isDropdownOpen) {
          setHighlightedIndex(prev => (prev - 1 + totalOptions) % totalOptions);
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (isDropdownOpen && highlightedIndex >= 0) {
          if (highlightedIndex < currentCompanies.length) {
            handleCompanySelect(currentCompanies[highlightedIndex]);
          } else if (showCreateOption) {
            handleCreateNew();
          }
        } else if (currentCompanies.length === 1) {
          handleCompanySelect(currentCompanies[0]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  const addAdminField = () => {
    setNewCompanyData(prev => ({
      ...prev,
      admins: [...prev.admins, ''],
    }));
  };

  const removeAdminField = (index: number) => {
    setNewCompanyData(prev => ({
      ...prev,
      admins: prev.admins.filter((_, i) => i !== index),
    }));
  };

  const updateAdminField = (index: number, email: string) => {
    setNewCompanyData(prev => ({
      ...prev,
      admins: prev.admins.map((admin, i) => (i === index ? email : admin)),
    }));
  };

  const handleCreateCompany = () => {
    const friendlyName = newCompanyData.friendly_name;
    const { admins } = newCompanyData;
    const primaryAdminEmail = newCompanyData.primary_admin_email;
    const { size, website, edx_course_org: edxCourseOrg } = newCompanyData;

    if (!friendlyName || !size || !edxCourseOrg.trim()) {
      return; // Basic validation
    }

    // Ensure primary admin is in the admins list
    const validAdmins = admins.filter(email => email.trim() !== '');
    if (primaryAdminEmail && !validAdmins.includes(primaryAdminEmail)) {
      validAdmins.push(primaryAdminEmail);
    }

    const companyData = {
      friendly_name: friendlyName,
      admins: validAdmins,
      ...(primaryAdminEmail && { primary_admin_email: primaryAdminEmail }),
      size,
      website: website || undefined,
      custom_csv_fields: [],
      custom_admin_portal: [],
      edx_course_org: edxCourseOrg.trim(),
    };

    onCompanyCreate(companyData);
    setShowInlineForm(false);
    setNewCompanyData({
      friendly_name: '',
      admins: [''],
      primary_admin_email: '',
      size: '',
      website: '',
      edx_course_org: '',
    });
    setSearchTerm(''); // Clear search term after creation
  };

  // Update search term when selectedCompany changes externally
  React.useEffect(() => {
    if (selectedCompany && !isDropdownOpen) {
      setSearchTerm(`${selectedCompany.friendly_name} (${selectedCompany.company_name})`);
    } else if (!selectedCompany && !isDropdownOpen && searchTerm.includes(' (') && searchTerm.includes(')')) {
      // If company was cleared but search term still looks like a selected company, clear it
      setSearchTerm('');
    }
  }, [selectedCompany, isDropdownOpen]);

  return (
    <div className="company-selector-container mb-4 w-100" ref={dropdownRef}>
      <Form.Group>
        <Form.Label>Select or Search for Company</Form.Label>
        <div className="searchable-dropdown">
          <InputGroup>
            <InputGroup.Prepend>
              <InputGroup.Text>
                <Search size="sm" />
              </InputGroup.Text>
            </InputGroup.Prepend>
            <Form.Control
              ref={inputRef}
              type="text"
              placeholder="Type to search companies or create new..."
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onKeyDown={handleKeyDown}
              disabled={isCreatingCompany}
              autoComplete="off"
            />
            <InputGroup.Append>
              {(searchTerm || selectedCompany) && (
                <InputGroup.Text
                  className="clear-search-icon"
                  onClick={() => {
                    setSearchTerm('');
                    onCompanySelect(null);
                    setIsDropdownOpen(true);
                    inputRef.current?.focus();
                  }}
                  style={{ cursor: 'pointer' }}
                  title="Clear search"
                >
                  ×
                </InputGroup.Text>
              )}
              <InputGroup.Text
                className="dropdown-toggle-icon"
                onClick={() => {
                  const newState = !isDropdownOpen;
                  setIsDropdownOpen(newState);
                  if (newState) {
                    inputRef.current?.focus();
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                <ExpandMore size="sm" />
              </InputGroup.Text>
            </InputGroup.Append>
          </InputGroup>

          {isDropdownOpen && (
            <div className="dropdown-menu show w-100 mt-1">
              {/* Show all companies when no search term, or filtered companies when searching */}
              {(!searchTerm ? companies : filteredCompanies).length > 0 && (
              <div className="dropdown-section">
                <div className="dropdown-header">Companies</div>
                {(!searchTerm ? companies : filteredCompanies).map((company, index) => (
                  <button
                    key={company.company_name}
                    type="button"
                    className={`dropdown-item ${index === highlightedIndex ? 'highlighted' : ''}`}
                    onClick={() => handleCompanySelect(company)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    <div className="company-option">
                      <div className="company-name">{company.friendly_name}</div>
                      <div className="company-code">({company.company_name})</div>
                    </div>
                  </button>
                ))}
              </div>
              )}

              {showCreateOption && (
              <div className="dropdown-section">
                {(!searchTerm ? companies : filteredCompanies).length > 0 && <div className="dropdown-divider" />}
                <button
                  type="button"
                  className={`dropdown-item create-new-item ${
                    highlightedIndex === (!searchTerm ? companies : filteredCompanies).length ? 'highlighted' : ''
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCreateNew();
                  }}
                  onMouseEnter={() => setHighlightedIndex((!searchTerm ? companies : filteredCompanies).length)}
                >
                  <div className="d-flex align-items-center">
                    <Add className="me-2" />
                    <span>{`Create "${searchTerm}"`}</span>
                  </div>
                </button>
              </div>
              )}

              {filteredCompanies.length === 0 && !showCreateOption && searchTerm && (
              <div className="dropdown-item text-muted">
                {`No companies found matching "${searchTerm}"`}
              </div>
              )}

              {!searchTerm && companies.length === 0 && (
              <div className="dropdown-item text-muted">
                No companies available
              </div>
              )}
            </div>
          )}
        </div>
      </Form.Group>

      {/* Inline Create Company Form */}
      {showInlineForm && (
      <div className="create-company-form p-4 border rounded bg-light mt-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Create New Company</h5>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => {
              setShowInlineForm(false);
              setSearchTerm('');
            }}
          >
            Cancel
          </Button>
        </div>

        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Company Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Example Company Name"
                  value={newCompanyData.friendly_name}
                  onChange={(e) => setNewCompanyData(prev => ({ ...prev, friendly_name: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Company Size <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="select"
                  value={newCompanyData.size}
                  onChange={(e) => setNewCompanyData(prev => ({ ...prev, size: e.target.value }))}
                >
                  <option value="">Select size...</option>
                  {COMPANY_SIZE_OPTIONS.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <Form.Group className="mb-3">
                <Form.Label>Website</Form.Label>
                <Form.Control
                  type="url"
                  placeholder="www.company.com"
                  value={newCompanyData.website}
                  onChange={(e) => setNewCompanyData(prev => ({ ...prev, website: e.target.value }))}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <Form.Group className="mb-3">
                <Form.Label>Course Authoring Organization: <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. UniversityX"
                  value={newCompanyData.edx_course_org}
                  onChange={(e) => setNewCompanyData(prev => ({ ...prev, edx_course_org: e.target.value }))}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <Form.Group className="mb-3">
                <Form.Label>Primary Admin Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="admin@company.com"
                  value={newCompanyData.primary_admin_email}
                  onChange={(e) => setNewCompanyData(prev => ({ ...prev, primary_admin_email: e.target.value }))}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <Form.Label>Admin Emails</Form.Label>
              {newCompanyData.admins.map((admin, index) => (
                <div key={admin || `admin-${index}`} className="d-flex mb-3">
                  <Form.Control
                    type="email"
                    placeholder="admin@company.com"
                    value={admin}
                    onChange={(e) => updateAdminField(index, e.target.value)}
                    className="me-2"
                  />
                  {newCompanyData.admins.length > 1 && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removeAdminField(index)}
                  >
                    Remove
                  </Button>
                  )}
                </div>
              ))}
              <Button
                variant="outline-primary"
                size="sm"
                onClick={addAdminField}
                iconBefore={Add}
                className="mb-3"
              >
                Add Admin
              </Button>
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <div className="d-flex">
                <Button
                  variant="primary"
                  onClick={handleCreateCompany}
                  disabled={(
                    !newCompanyData.friendly_name
                    || !newCompanyData.size
                    || !newCompanyData.edx_course_org
                    || isCreatingCompany
                  )}
                  style={{ marginRight: '16px' }}
                >
                  {isCreatingCompany ? 'Creating...' : 'Create Company'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowInlineForm(false);
                    setSearchTerm('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </div>
      )}
    </div>
  );
};

export default CompanySelector;
